import React, { useState, useEffect } from 'react';
import {
    Table,
    Typography,
    Button,
    Space,
    Modal,
    message,
    Tooltip,
    Form,
    Input,
    Divider, Tag, Select
} from 'antd';
import {
    InfoCircleOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';


const { Title, Text } = Typography;
const { TextArea } = Input;

const AgentList = ({ user }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingAgent, setEditingAgent] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchAgents();
    }, [user]);


    const paginationConfig = {
        defaultPageSize: 10,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
        pageSizeOptions: ['5', '10', '20', '50'],
    };


    const fetchAgents = async () => {
        setLoading(true);
        try {
            // 사용자의 에이전트와 기본 에이전트를 함께 조회
            const { data: userAgents, error: userError } = await supabase
                .from('agents')
                .select(`
                    *,
                    agent_template:agent_templates (
                        title,
                        value,
                        description,
                        category,
                        required_fields
                    )
                `)
                .eq('created_by', user.id);

            const { data: defaultAgents, error: defaultError } = await supabase
                .from('agents')
                .select(`
                    *,
                    agent_template:agent_templates (
                        title,
                        value,
                        description,
                        category,
                        required_fields
                    )
                `)
                .eq('is_default', true);

            if (userError || defaultError) throw (userError || defaultError);
            setTemplates([...userAgents, ...defaultAgents]);
        } catch (error) {
            message.error(`에이전트 로딩 실패: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (templateId) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this template?',
            content: 'Deleted templates cannot be recovered.',
            onOk: async () => {
                try {
                    const { error } = await supabase
                        .from('agents')
                        .delete()
                        .eq('id', templateId);

                    if (error) throw error;

                    message.success('Template successfully deleted.');
                    fetchAgents();
                } catch (error) {
                    message.error(`Template deletion failed: ${error.message}`);
                }
            }
        });
    };

    const handleEditAgent = async (values) => {
        try {
            // config에서 knowledge로 시작하는 키는 유지
            const currentConfig = editingAgent.config || {};
            const newConfig = values.config || {};

            const preservedKnowledge = Object.entries(currentConfig)
                .filter(([key]) => key.startsWith('knowledge'))
                .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

            const updatedConfig = {
                ...preservedKnowledge,
                ...newConfig
            };

            const { error } = await supabase
                .from('agents')
                .update({
                    name: values.name,
                    description: values.description,
                    config: updatedConfig
                })
                .eq('id', editingAgent.id);

            if (error) throw error;
            message.success('에이전트가 성공적으로 수정되었습니다.');
            fetchAgents();
            setEditingAgent(null);
        } catch (error) {
            message.error(`에이전트 수정 실패: ${error.message}`);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <>
                    {text}
                    {record.is_default && <Text type="secondary"> (default)</Text>}
                </>
            )
        },
        {
            title: 'Agent Type',
            dataIndex: ['agent_template', 'value'],
            key: 'value',
            render: (value) => {
                const typeConfig = {
                    customerSupportAdvisor: { color: 'orange', text: 'Customer Support Advisor' },
                    presentatorAdvisor: { color: 'magenta', text: 'Presentation Advisor' },
                    remoteWorking: { color: 'blue', text: 'Remote Working Advisor' },
                    conflictResolutionAdvisor: { color: 'red', text: 'Conflict Resolution Advisor' },
                    others: { color: 'default', text: 'Others' },
                    salesAdvisor: { color: 'cyan', text: 'Sales Advisor' },
                    teamCollaborationAdvisor: { color: 'geekblue', text: 'Team Collaboration Advisor' },
                    negotiationAdvisor: { color: 'volcano', text: 'Negotiation Advisor' },
                    jobInterview: { color: 'green', text: 'Job Interview Advisor' },
                };

                const config = typeConfig[value] || { color: 'default', text: value };
                return (
                    <Tag color={config.color} style={{ borderRadius: '12px', padding: '0 10px', display: 'flex', justifyContent: 'center' }}>
                        {config.text}
                    </Tag>
                );
            }
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => {
                if (!text) return '-';
                const words = text.split(' ');
                return words.length > 5 ? `${words.slice(0, 5).join(' ')}...` : text;
            }
        },
        {
            title: 'Created Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleString(),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {!record.is_default && (
                        <>
                            <Tooltip title="Edit">
                                <Button
                                    icon={<EditOutlined />}
                                    onClick={() => setEditingAgent(record)}
                                />
                            </Tooltip>
                            <Tooltip title="Delete">
                                <Button
                                    icon={<DeleteOutlined />}
                                    danger
                                    onClick={() => handleDelete(record.id)}
                                />
                            </Tooltip>
                        </>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginBottom: 16
            }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/agent/create')}
                >
                    Personalize My Assistant
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={templates}
                loading={loading}
                rowKey="id"
                pagination={paginationConfig}
            />

            {/* Edit Template Modal */}
            {editingAgent && (
                <Modal
                    title="에이전트 수정"
                    visible={!!editingAgent}
                    onCancel={() => setEditingAgent(null)}
                    footer={null}
                >
                    <Form
                        initialValues={{
                            name: editingAgent.name,
                            description: editingAgent.description,
                            ...Object.entries(editingAgent.config || {})
                                .filter(([key]) => !key.startsWith('knowledge'))
                                .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
                        }}
                        onFinish={(values) => {
                            const { name, description, ...configValues } = values;
                            handleEditAgent({
                                name,
                                description,
                                config: configValues
                            });
                        }}
                    >
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="description"
                            label="Description"
                        >
                            <TextArea rows={3} />
                        </Form.Item>

                        <Divider>Settings</Divider>

                        {editingAgent.agent_template?.required_fields?.fields?.map(field => {
                            // knowledge로 시작하는 필드는 건너뛰기
                            if (field.type === 'upload') {
                                const knowledgeIds = editingAgent.config?.[`knowledge_${field.name}_ids`] || [];
                                return (
                                    <Form.Item
                                        key={field.name}
                                        label={field.label}
                                    >
                                        <Input
                                            disabled
                                            value={`${knowledgeIds.length}개의 파일이 업로드됨`}
                                            prefix={<InfoCircleOutlined />}
                                        />
                                    </Form.Item>
                                );
                            }
                            switch (field.type) {
                                case 'input':
                                    return (
                                        <Form.Item
                                            key={field.name}
                                            name={field.name}
                                            label={field.label}
                                            rules={[{ required: field.required }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                    );
                                case 'textarea':
                                    return (
                                        <Form.Item
                                            key={field.name}
                                            name={field.name}
                                            label={field.label}
                                            rules={[{ required: field.required }]}
                                        >
                                            <TextArea rows={4} />
                                        </Form.Item>
                                    );
                                case 'select':
                                    return (
                                        <Form.Item
                                            key={field.name}
                                            name={field.name}
                                            label={field.label}
                                            rules={[{ required: field.required }]}
                                        >
                                            <Select options={field.options} />
                                        </Form.Item>
                                    );
                                default:
                                    return null;
                            }
                        })}

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Submit
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            )}
        </div>
    );
};

export default AgentList;